//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import React, { useCallback, useMemo, useRef } from 'react';
import { Input, Popover } from 'antd';
import { Icon } from 'antd';
import { SelectValue } from 'antd/lib/select';
import cx from 'classnames';
import { debounce } from 'lodash';

import { PicklistValue } from 'components/inputs/types';
import { Text } from 'components/typography';
import AppConstants from 'utils/AppConstants';

import FuzzyDropdown from './components/FuzzyDropdown';
import { useFuzzySelection } from './hooks/useFuzzySelection';
import { useFuzzyState } from './hooks/useFuzzyState';
import { FuzzyOptionState, FuzzySelectProps } from './types';
import { getDisplayLabel, parseFuzzyValue } from './utils/fuzzyValueHelpers';

import './FuzzySelect.less';

/**
 * Helper: Get display value for readonly mode
 */
const getReadonlyDisplayValue = (
  value: SelectValue | undefined,
  defaultValue: SelectValue | undefined,
  values?: PicklistValue[]
): string => {
  if (!values) {
    return value?.toString() || defaultValue?.toString() || '';
  }

  const currentVal = value ?? defaultValue;
  const valStr = typeof currentVal === 'string' ? currentVal : String(currentVal || '');
  const { value: baseValue, percentage } = parseFuzzyValue(valStr);
  const matchedOption = values.find((item) => item.value === baseValue || item.value === currentVal);

  if (matchedOption) {
    if (percentage !== null) {
      if (matchedOption.hasMinMaxTypoConfig) {
        const typoText = percentage === 1 ? 'typo' : 'typos';
        return `${matchedOption.label} (Max ${percentage} ${typoText})`;
      }
      return `${matchedOption.label} (${percentage}%)`;
    }
    return matchedOption.label;
  }

  return values
    .filter((item) => item.value === currentVal)
    .map(({ label }) => label)
    .join(', ');
};

/**
 * Helper: Restore fuzzy option state when opening popover
 */
const restoreFuzzyOptionState = (
  selectedValue: SelectValue | undefined,
  values: PicklistValue[] | undefined,
  optionData: PicklistValue[]
): FuzzyOptionState | null => {
  if (!selectedValue) return null;

  const valStr = typeof selectedValue === 'string' ? selectedValue : String(selectedValue);
  const { value: baseValue, percentage, typoCount } = parseFuzzyValue(valStr);

  if (percentage === null && typoCount === null) return null;

  const optionsToUse = values || optionData;
  const option = optionsToUse?.find((opt) => opt.value === baseValue);

  if (option && (option.hasFuzzyOption || option.hasMinMaxTypoConfig)) {
    return { option, percentage, typoCount };
  }

  return null;
};

/**
 * FuzzySelect is an enhanced select component that supports fuzzy matching
 * Allows users to select options with configurable matching parameters:
 * - Percentage-based fuzzy matching (0-100%)
 * - Typo tolerance (1-4 typos)
 *
 * @example
 * ```tsx
 * <FuzzySelect
 *   optionData={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   placeholder="Select an option"
 * />
 * ```
 */
const FuzzySelect: React.FC<FuzzySelectProps> = ({
  children,
  className,
  displayMode,
  optionData = [],
  placeholder = 'Select',
  useDataTypeBadges = false,
  values,
  value,
  defaultValue,
  disabled = false,
  onChange,
  onSearchChange,
  isTokenInput,
}) => {
  const inputRef = useRef<Input>(null);

  // Use custom hooks for state management
  const {
    isOpen,
    setIsOpen,
    searchValue,
    setSearchValue,
    selectedValue,
    setSelectedValue,
    expandedFuzzyOption,
    setExpandedFuzzyOption,
  } = useFuzzyState(value, defaultValue, values, optionData, onChange);

  const { handleOptionSelect, handleSliderChange } = useFuzzySelection(
    selectedValue,
    expandedFuzzyOption,
    setSelectedValue,
    setExpandedFuzzyOption,
    setIsOpen,
    setSearchValue,
    onChange
  );

  // Memoized display label for selected value
  const displayLabel = useMemo(() => getDisplayLabel(selectedValue, values || optionData), [
    selectedValue,
    values,
    optionData,
  ]);

  // Handle popover visibility changes
  const handleVisibilityChange = useCallback(
    (visible: boolean) => {
      if (!visible) {
        setExpandedFuzzyOption(null);
        setSearchValue('');
      } else if (visible) {
        const fuzzyState = restoreFuzzyOptionState(selectedValue, values, optionData);
        if (fuzzyState) {
          setExpandedFuzzyOption(fuzzyState);
        }
      }
      setIsOpen(visible);
    },
    [selectedValue, values, optionData, setExpandedFuzzyOption, setSearchValue, setIsOpen]
  );

  // Debounced search callback
  const debouncedSearchChange = useMemo(
    () =>
      debounce((searchText: string) => {
        onSearchChange?.(searchText);
      }, 300),
    [onSearchChange]
  );

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (!inputValue) {
        setSearchValue('');
        setSelectedValue('');
        setExpandedFuzzyOption(null);
        onChange?.('');
        debouncedSearchChange.cancel();
        onSearchChange?.('');
        return;
      }
      setSearchValue(inputValue);
      debouncedSearchChange(inputValue);
    },
    [setSearchValue, setSelectedValue, setExpandedFuzzyOption, onChange, onSearchChange, debouncedSearchChange]
  );

  // Handle input click
  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled, setIsOpen]);

  // Early return for readonly mode
  if (displayMode === AppConstants.INPUT_DISPLAY_MODE.READONLY) {
    const displayValue = getReadonlyDisplayValue(value, defaultValue, values);
    return <Text>{displayValue}</Text>;
  }

  // Render dropdown content
  const dropdownContent = (
    <FuzzyDropdown
      optionData={optionData}
      values={values}
      useDataTypeBadges={useDataTypeBadges}
      searchValue={searchValue}
      selectedValue={selectedValue}
      expandedFuzzyOption={expandedFuzzyOption}
      isTokenInput={isTokenInput}
      defaultValue={defaultValue}
      onOptionSelect={handleOptionSelect}
      onSliderChange={handleSliderChange}>
      {children}
    </FuzzyDropdown>
  );

  return (
    <Popover
      content={dropdownContent}
      trigger="click"
      visible={isOpen}
      onVisibleChange={handleVisibilityChange}
      placement="bottomLeft"
      overlayClassName="fuzzy-select-popover"
      getPopupContainer={(trigger) => trigger.parentElement || document.body}>
      <div
        className={cx('fuzzy-select', 'synri-select', className, { 'fuzzy-select--disabled': disabled })}
        data-testid="fuzzy-select">
        <Input
          ref={inputRef}
          className="fuzzy-select-input"
          placeholder={placeholder}
          value={searchValue || displayLabel}
          disabled={disabled}
          onChange={handleSearchChange}
          onClick={handleInputClick}
          suffix={<Icon type={isOpen ? 'up' : 'down'} className="fuzzy-select-arrow-icon" />}
          data-testid="fuzzy-select-input"
        />
      </div>
    </Popover>
  );
};

export default FuzzySelect;
